import { useState } from 'react';
import { IBlogPageViewModel } from 'Types/Litium';
import tailwindBreakpoints from 'Utils/tailwindBreakpoints';
import Markup from 'Components/Markup';

interface BlogMainItemProps {
    horizontalDividerImage: IBlogPageViewModel['horizontalDividerImage'];
    horizontalDividerImageMobile: IBlogPageViewModel['horizontalDividerImageMobile'];
    expandMainBlogItemBodyText: IBlogPageViewModel['expandMainBlogItemBodyText'];
    writtenByText: IBlogPageViewModel['writtenByText'];
    authorLinkBase: IBlogPageViewModel['authorLinkBase'];
    mainBlogItem: IBlogPageViewModel['mainBlogItem'];
}

const BlogMainItem = ({
    horizontalDividerImage,
    horizontalDividerImageMobile,
    expandMainBlogItemBodyText,
    writtenByText,
    authorLinkBase,
    mainBlogItem,
}: BlogMainItemProps) => {
    const bodyTextExceedsThreshold =
        mainBlogItem?.bodyText?.value?.length > 2700;

    const [showEntireBodyText, setShowBodyText] = useState(false);

    const handleReadMoreButtonClick = () => {
        setShowBodyText(true);
    };

    const renderedCommentsScript = {
        __html: mainBlogItem.commentsScript,
    };

    const postDateAndWrittenByText = `${mainBlogItem.postDate} ${writtenByText} `;

    const authorLink =
        authorLinkBase + mainBlogItem.author.toLowerCase().replaceAll(' ', '');

    return (
        <div>
            <picture>
                <source
                    width={
                        mainBlogItem.topImage.width > 0
                            ? mainBlogItem.topImage.width
                            : ''
                    }
                    height={
                        mainBlogItem.topImage.height > 0
                            ? mainBlogItem.topImage.height
                            : ''
                    }
                    media={`(min-width: ${tailwindBreakpoints.lg})`}
                    srcSet={mainBlogItem.topImage.src}
                />
                <img
                    className="w-full"
                    src={mainBlogItem.topImageMobile.src}
                    alt={mainBlogItem.topImageMobile.altText}
                    width={
                        mainBlogItem.topImageMobile.width > 0
                            ? mainBlogItem.topImageMobile.width
                            : ''
                    }
                    height={
                        mainBlogItem.topImageMobile.height > 0
                            ? mainBlogItem.topImageMobile.height
                            : ''
                    }
                />
            </picture>

            <span className="font-roboto text-xs font-semibold">
                {postDateAndWrittenByText}
                <a href={authorLink} className="underline">
                    {mainBlogItem.author}
                </a>
            </span>

            <div className="relative">
                <h1 className="mb-2.5 mt-2 font-montserrat text-2xl font-medium">
                    {mainBlogItem.headerText}
                </h1>

                <div
                    className={`relative grid overflow-clip transition-all duration-layout ${
                        showEntireBodyText
                            ? 'grid-rows-[1fr]'
                            : 'grid-rows-[0fr]'
                    } `}
                >
                    <div
                        className={`${
                            bodyTextExceedsThreshold ? 'min-h-[600px]' : ''
                        }`}
                    >
                        <Markup
                            html={mainBlogItem.bodyText?.value}
                            as="article"
                            cssClass="not-prose"
                        />
                    </div>
                </div>

                {bodyTextExceedsThreshold && (
                    <div
                        className={`absolute bottom-0 w-full overflow-hidden bg-gradient-to-t from-[var(--bg-color)] from-20% to-transparent transition-all duration-layout ${
                            showEntireBodyText ? 'h-0' : 'h-20'
                        }`}
                    />
                )}
            </div>

            {bodyTextExceedsThreshold && (
                <button
                    className={`transition-all duration-layout ${
                        showEntireBodyText ? 'opacity-0' : ''
                    }`}
                    onClick={() => handleReadMoreButtonClick()}
                >
                    {expandMainBlogItemBodyText}
                </button>
            )}

            <picture>
                <source
                    media={`(min-width: ${tailwindBreakpoints.lg})`}
                    srcSet={horizontalDividerImage.src}
                    width={
                        horizontalDividerImage.width > 0
                            ? horizontalDividerImage.width
                            : ''
                    }
                    height={
                        horizontalDividerImage.height > 0
                            ? horizontalDividerImage.height
                            : ''
                    }
                />
                <img
                    className="my-4 w-full"
                    src={horizontalDividerImageMobile.src}
                    alt={horizontalDividerImageMobile.altText}
                    width={
                        horizontalDividerImageMobile.width > 0
                            ? horizontalDividerImageMobile.width
                            : ''
                    }
                    height={
                        horizontalDividerImageMobile.height > 0
                            ? horizontalDividerImageMobile.height
                            : ''
                    }
                />
            </picture>

            <div dangerouslySetInnerHTML={renderedCommentsScript} />
        </div>
    );
};

export default BlogMainItem;
